import React, { useState, useEffect, useMemo, useCallback } from 'react'; // Added useCallback
import ShapeMap from './ShapeMap';
import Instructions from './Instructions';
import CharacterProfile from './CharacterProfile';
import Inventory from './Inventory';
import HealthBar from './HealthBar';
import Quests from './Quests';
import DialogueBubble from './DialogueBubble';
import AudioManager from './AudioManager';
import { getNpcsByZone, getQuestsByZone, getQuestById } from '../data/npcInteractions';
import { zones } from '../data/zones';
import { getMonstersByZone } from '../data/monsters';
import { achievements } from './achievements'; // If it's still in src/components
import { waypoints, isWaypointUnlocked } from '../data/waypoints';
import Hub from './Hub';
import TouchControls from './TouchControls';
import {
  combat,
  applyLoot,
  getLootStatic,
  calculateXP,
  generateFloor,
  generateZone,
  spawnEnemy,
} from '../utils/gameLogic';

// Replacement for generateFloor (uses static data)
const generateFloorStatic = (playerLevel, zoneId = zones[0].id, floorsCleared = 0) => {
  const width = 10;
  const height = 10;
  const floor = Array.from({ length: height }, () => Array(width).fill('floor'));

  // Add walls
  for (let i = 0; i < height; i++) {
    floor[i][0] = 'wall';
    floor[i][width - 1] = 'wall';
  }
  for (let j = 0; j < width; j++) {
    floor[0][j] = 'wall';
    floor[height - 1][j] = 'wall';
  }

  // Add loot
  floor[2][2] = 'loot';

  // Determine if we should place an exit or portal based on floorsCleared
  const zone = zones.find(z => z.id === zoneId) || zones[0];
  const levelRangeMin = zone.level_range_min || 1;
  const levelRangeMax = zone.level_range_max || 5;
  const maxFloors = levelRangeMax - levelRangeMin + 1;

  const exitX = width - 2;
  const exitY = height - 2;
  if (exitY >= 0 && exitY < height && exitX >= 0 && exitX < width) {
    floor[exitY][exitX] = floorsCleared >= maxFloors - 1 ? 'portal' : 'exit';
  } else {
    console.error('Exit/Portal position out of bounds:', { exitX, exitY, width, height });
  }

  // Add NPC
  const zoneNpcs = getNpcsByZone(zoneId);
  let npc = null;
  if (zoneNpcs.length > 0) {
    const selectedNpc = zoneNpcs[Math.floor(Math.random() * zoneNpcs.length)];
    const npcX = selectedNpc.position_x || 3;
    const npcY = selectedNpc.position_y || 4;
    if (
      npcY >= 0 &&
      npcY < height &&
      npcX >= 0 &&
      npcX < width &&
      floor[npcY][npcX] === 'floor' &&
      !(npcX === exitX && npcY === exitY) &&
      !(npcX === 2 && npcY === 2)
    ) {
      floor[npcY][npcX] = 'npc';
      npc = { x: npcX, y: npcY, data: selectedNpc };
    }
  }

  return { floor, npc, zoneId };
};

// Replacement for generateZone (uses static data)
const generateZoneStatic = (playerLevel, currentZoneId = null) => {
  const eligibleZones = zones.filter(
    (z) => playerLevel >= z.level_range_min && playerLevel <= z.level_range_max
  );
  const currentZoneIndex = currentZoneId ? zones.findIndex((z) => z.id === currentZoneId) : -1;
  const nextZone = eligibleZones[Math.floor(Math.random() * eligibleZones.length)];
  return nextZone || zones[0];
};

// Replacement for spawnEnemy (uses static data)
const spawnEnemyStatic = (playerLevel, zoneEffect, zoneId) => {
  const zoneMonsters = getMonstersByZone(zoneId);
  if (zoneMonsters.length === 0) return [];

  // Determine the number of enemies to spawn (1 to 3)
  const spawnRoll = Math.random();
  let numEnemies;
  if (spawnRoll < 0.5) numEnemies = 1; // 50% chance for 1 enemy
  else if (spawnRoll < 0.8) numEnemies = 2; // 30% chance for 2 enemies
  else numEnemies = 3; // 20% chance for 3 enemies

  // Decide if enemies are the same type or a mixture
  const sameType = Math.random() < 0.5; // 50% chance for same type, 50% for mixed types

  const enemies = [];
  const mapSize = zoneMonsters.some(m => m.isBoss) ? 15 : 10; // Use larger map size for bosses

  for (let i = 0; i < numEnemies; i++) {
    let selectedMonster;
    if (sameType && i > 0) {
      selectedMonster = enemies[0].monsterData;
    } else {
      selectedMonster = zoneMonsters[Math.floor(Math.random() * zoneMonsters.length)];
    }

    let hp = Math.max(1, selectedMonster.base_hp + playerLevel * 0.5);
    let atk = Math.max(1, selectedMonster.base_atk + playerLevel * 0.2);
    let def = Math.max(1, selectedMonster.base_def + playerLevel * 0.1);
    let mana = Math.max(0, (selectedMonster.base_mana || 0) + playerLevel * 0.3);

    // Round stats to whole numbers
    hp = Math.round(hp);
    atk = Math.round(atk);
    def = Math.round(def);
    mana = Math.round(mana);

    // Apply zone effects
    switch (zoneEffect) {
      case 'blood':
        hp += 5;
        break;
      case 'fire':
        atk += 2;
        break;
      case 'earth':
        def += 2;
        break;
      case 'illusion':
        def += 1;
        atk -= 1;
        break;
      case 'darkness':
        atk += 1;
        break;
      case 'storm':
        hp += 2;
        atk += 1;
        break;
      case 'poison':
        hp += 3;
        break;
      default:
        break;
    }

    // Round again after zone effects
    hp = Math.round(hp);
    atk = Math.round(atk);
    def = Math.round(def);

    // Ensure unique positions for each enemy
    let x, y;
    let attempts = 0;
    const maxAttempts = 50;
    do {
      x = Math.floor(Math.random() * (mapSize - 2)) + 1;
      y = Math.floor(Math.random() * (mapSize - 2)) + 1;
      attempts++;
    } while (
      enemies.some(e => e.x === x && e.y === y) &&
      attempts < maxAttempts
    );

    if (attempts >= maxAttempts) {
      continue;
    }

    enemies.push({
      x,
      y,
      type: selectedMonster.name,
      hp,
      maxHp: hp,
      mana,
      maxMana: mana,
      atk,
      def,
      isBoss: selectedMonster.isBoss || false,
      level_min: selectedMonster.level_min,
      level_max: selectedMonster.level_max,
      rarity: selectedMonster.rarity,
      monsterData: selectedMonster,
    });
  }

  return enemies;
};

function Game({ setGameState, updateLeaderboard, selectedClass, characterId, shareOnX, setPlayerStats }) {
  const [player, setPlayer] = useState(null);
  const [floorData, setFloorData] = useState(null);
  const [zone, setZone] = useState(null);
  const [enemies, setEnemies] = useState([]); // Updated from enemy to enemies
  const [npc, setNpc] = useState(null);
  const [combatLog, setCombatLog] = useState([]);
  const [gameLog, setGameLog] = useState([]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showHub, setShowHub] = useState(false);
  const [lastCombatEnd, setLastCombatEnd] = useState(0);
  const [combatFlash, setCombatFlash] = useState(null);
  const [showDialogue, setShowDialogue] = useState(false);
  const [lastRegen, setLastRegen] = useState(Date.now());
  const [lastZoneEffect, setLastZoneEffect] = useState(Date.now());
  const [isLoading, setIsLoading] = useState(true);
  const [soulEffectActive, setSoulEffectActive] = useState(false);
  const [selectedInventoryIndex, setSelectedInventoryIndex] = useState(-1);
  const [needsLeaderboardUpdate, setNeedsLeaderboardUpdate] = useState(false);
  const [lastEnemyMove, setLastEnemyMove] = useState(Date.now());
  const [showBaseUpgrades, setShowBaseUpgrades] = useState(true);
  const [showAdvancedUpgrades, setShowAdvancedUpgrades] = useState(false);
  const [showEliteUpgrades, setShowEliteUpgrades] = useState(false);
  const [showMasterUpgrades, setShowMasterUpgrades] = useState(false);
  const [showLegendaryUpgrades, setShowLegendaryUpgrades] = useState(false);
  const [showTempBoosts, setShowTempBoosts] = useState(false);
  const [showQuests, setShowQuests] = useState(false);
  const [showInventory, setShowInventory] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showWaypoints, setShowWaypoints] = useState(false);

  const [mobKilledTrigger, setMobKilledTrigger] = useState(false);
  const [attackTrigger, setAttackTrigger] = useState(false);
  const [enemyAttackTrigger, setEnemyAttackTrigger] = useState(false);
  const [moveTrigger, setMoveTrigger] = useState(false);
  const [stairsTrigger, setStairsTrigger] = useState(false);
  const [pickupTrigger, setPickupTrigger] = useState(false);

  const [isMobile, setIsMobile] = useState(window.matchMedia("(max-width: 768px)").matches); // Add this line

  const calculateInitialStamina = useMemo(() => {
    return (className) => {
      switch (className) {
        case 'Warrior': return 100;
        case 'Rogue': return 80;
        case 'Mage': return 90;
        default: return 100;
      }
    };
  }, []);

  // Add this useEffect after the existing useEffect hooks for player stats and leaderboard
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleResize = (e) => setIsMobile(e.matches);

    mediaQuery.addEventListener("change", handleResize);
    return () => mediaQuery.removeEventListener("change", handleResize);
  }, []);

  const savePlayerStats = async () => {
    if (!player || !player.playerId || !characterId) {
      setGameLog((prev) => [...prev, 'Failed to save game progress: Missing player or character ID']);
      return;
    }
    if (window.isSaving) {
      console.log('Save already in progress, skipping...');
      return;
    }
    window.isSaving = true;
    try {
      const payload = {
        character_id: characterId,
        player_id: player.playerId,
        character_name: sanitizeInput(player.character_name || 'Unnamed'),
        class: player.class || 'Warrior',
        is_alive: !player.isDead,
        last_played: new Date().toISOString(),
        floors_cleared: player.floorsCleared || 0,
        kills: player.kills || 0,
        hp: player.hp || 0,
        max_hp: player.maxHp || 10,
        stamina: player.stamina || calculateInitialStamina(player.class || 'Warrior'),
        atk: player.atk || 2,
        def: player.def || 1,
        level: player.level || 1,
        xp: player.xp || 0,
        inventory: player.inventory || [],
        equipped: {
          head: player.equipped.head,
          chest: player.equipped.chest,
          hands: player.equipped.hands,
          feet: player.equipped.feet,
          offHand: player.equipped.offHand,
          weapon: player.equipped.weapon,
          neck: player.equipped.neck,
          rings: player.equipped.rings,
        },
        active_quests: player.activeQuests.map((q) => ({ ...q, progress: q.progress || 0 })) || [],
        crit_chance: player.crit_chance || 0.05,
        crit_damage: player.crit_damage || 1.5,
        mana: player.mana || 10,
        max_mana: player.max_mana || 10,
        dodge_chance: player.dodge_chance || 0.05,
        status_resist: player.status_resist || 0.1,
        luck: player.luck || 1,
        gold: player.gold || 0,
        ectoplasm: player.ectoplasm || 0,
        tempBoosts: player.tempBoosts || {},
        npcInteractions: player.npcInteractions || {},
        killsByType: player.killsByType || {},
        killsByRarity: player.killsByRarity || {},
        killsByName: player.killsByName || {},
        zoneVisited: player.zoneVisited || [],
        questsCompleted: player.questsCompleted || 0,
        questsCompletedInZone: player.questsCompletedInZone || {},
        npcTrades: player.npcTrades || 0,
        itemsUsedOrEquipped: player.itemsUsedOrEquipped || 0,
        achievements: player.achievements || [],
        lastWaypointTravel: player.lastWaypointTravel || {}, // Save waypoint travel cooldowns
        unlockedWaypoints: player.unlockedWaypoints || [], // Save unlocked waypoints
      };
  
      const response = await authFetch(
        `https://cryptwalkergame-worker.patrick-vida.workers.dev/api/players?player_id=${player.playerId}`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      );
  
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save player stats: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error saving player stats:', error);
      setGameLog((prev) => [...prev, `Failed to save game progress: ${error.message}`]);
    } finally {
      window.isSaving = false;
    }
  };

  const checkAchievements = () => {
    const newAchievements = [];
    achievements.forEach((achievement) => {
      if (player.achievements?.includes(achievement.id)) return; // Skip already completed achievements
  
      let currentValue = 0;
      if (achievement.target === "kills") currentValue = player.kills || 0;
      else if (achievement.target === "killsByType") currentValue = player.killsByType[achievement.targetSubType] || 0;
      else if (achievement.target === "killsByRarity") currentValue = player.killsByRarity[achievement.targetSubType] || 0;
      else if (achievement.target === "killsByName") currentValue = player.killsByName[achievement.targetSubType] || 0;
      else if (achievement.target === "floorsCleared") currentValue = player.floorsCleared || 0;
      else if (achievement.target === "zoneVisited") currentValue = player.zoneVisited.includes(achievement.zoneRegion) ? 1 : 0;
      else if (achievement.target === "questsCompleted") currentValue = player.questsCompleted || 0;
      else if (achievement.target === "questsCompletedInZone") currentValue = player.questsCompletedInZone[achievement.zoneId] || 0;
      else if (achievement.target === "gold") currentValue = player.gold || 0;
      else if (achievement.target === "ectoplasm") currentValue = player.ectoplasm || 0;
      else if (achievement.target === "level") currentValue = player.level || 1;
      else if (achievement.target === "npcTrades") currentValue = player.npcTrades || 0;
      else if (achievement.target === "itemsUsedOrEquipped") currentValue = player.itemsUsedOrEquipped || 0;
  
      // Check zone-specific conditions
      if (achievement.zoneId && zone.id !== achievement.zoneId) return;
      if (achievement.zoneRegion && !player.zoneVisited.includes(achievement.zoneRegion)) return;
  
      if (currentValue >= achievement.targetValue) {
        newAchievements.push(achievement);
        setGameLog((prev) => [...prev, `Achievement Unlocked: ${achievement.name}!`]);
  
        // Apply reward
        setPlayer((prev) => {
          let newPlayer = { ...prev };
          if (achievement.reward.type === "gold") {
            newPlayer.gold += achievement.reward.value;
          } else if (achievement.reward.type === "ectoplasm") {
            newPlayer.ectoplasm += achievement.reward.value;
          } else if (achievement.reward.type === "item" && newPlayer.inventory.length < 5) {
            newPlayer.inventory = [...newPlayer.inventory, achievement.reward];
          }
          newPlayer.achievements = [...(newPlayer.achievements || []), achievement.id];
          return newPlayer;
        });
      }
    });
  };

  const applyBomb = async (bomb) => {
    if (!enemies || enemies.length === 0 || !bomb) return;

    let closestEnemy = null;
    let minDistance = Infinity;
    let closestEnemyIndex = -1;

    enemies.forEach((enemy, index) => {
      const distance = Math.abs(player.x - enemy.x) + Math.abs(player.y - enemy.y);
      if (distance < minDistance) {
        minDistance = distance;
        closestEnemy = enemy;
        closestEnemyIndex = index;
      }
    });

    const damage = bomb.damage || 0;
    const newEnemyHp = Math.max(0, closestEnemy.hp - damage);
    const updatedEnemies = [...enemies];
    updatedEnemies[closestEnemyIndex] = { ...closestEnemy, hp: newEnemyHp };
    setEnemies(updatedEnemies);

    if (newEnemyHp <= 0) {
      const loot = await applyLoot(player);
      const xpGainedRaw = calculateXP(closestEnemy, player.level || 1);
      const xpGained = isNaN(xpGainedRaw) ? 5 : xpGainedRaw;

      setCombatLog((prev) => [
        ...prev,
        `${closestEnemy.type} defeated with ${bomb.name}! Gained ${loot.gold} gold and ${xpGained} XP!`,
      ]);

      const currentXp = !isNaN(player.xp) ? player.xp : 0;
      const newXp = currentXp + xpGained;
      let newLevel = !isNaN(player.level) && player.level > 0 ? player.level : 1;
      let newMaxHp = player.maxHp;
      let newAtk = player.atk;
      let newDef = player.def;

      if (newXp >= newLevel * 10) {
        newLevel++;
        newMaxHp += 5;
        newAtk += 1;
        newDef += 1;
        setCombatLog((prev) => [...prev, `Level up! You are now level ${newLevel}!`]);
      }

      setPlayer((prev) => ({
        ...prev,
        hp: newMaxHp,
        maxHp: newMaxHp,
        atk: newAtk,
        def: newDef,
        gold: loot.player.gold,
        ectoplasm: loot.player.ectoplasm,
        inventory: loot.player.inventory.filter((item) => item !== bomb),
        kills: (prev.kills || 0) + 1,
        level: newLevel,
        xp: newLevel > (player.level || 1) ? newXp % ((player.level || 1) * 10) : newXp,
      }));

      const remainingEnemies = updatedEnemies.filter(e => e.hp > 0);
      setEnemies(remainingEnemies);

      if (remainingEnemies.length === 0) {
        setTimeout(() => {
          const newEnemies = spawnEnemyStatic(player.level, zone.effect, zone.id);
          if (newEnemies.length > 0) {
            setEnemies(newEnemies);
            setGameLog((prev) => [...prev, `${newEnemies.length} enemies have appeared: ${newEnemies.map(e => e.type).join(', ')}!`]);
          }
        }, 5000);
      }
    }
  };

  const getToken = () => localStorage.getItem('token');

  const authFetch = async (url, options = {}) => {
    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found. Please log in.');
    }
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
    };
    const response = await fetch(url, { ...options, headers });
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('characterId');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('currentUsername');
      setGameState('start');
      throw new Error('Session expired. Please log in again.');
    }
    return response;
  };

  const sanitizeInput = (input) => {
    if (typeof input !== 'string') return input;
    return input.trim().replace(/[<>"'%;()&+]/g, '');
  };

  useEffect(() => {
    if (player && setPlayerStats) {
      setPlayerStats(player);
    }
  }, [player, setPlayerStats]);

  useEffect(() => {
    if (needsLeaderboardUpdate && player && player.playerId && updateLeaderboard) {
      updateLeaderboard(player, player.floorsCleared || 0);
      setNeedsLeaderboardUpdate(false);
    }
  }, [needsLeaderboardUpdate, player, updateLeaderboard]);

  useEffect(() => {
    if (!player || !characterId) return;

    const debounceSave = setTimeout(() => {
      savePlayerStats();
    }, 1000);

    return () => clearTimeout(debounceSave);
  }, [player, characterId]);

  useEffect(() => {
    if (!player || !characterId) return;

    const handleBeforeUnload = () => {
      savePlayerStats();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [player, characterId]);

  useEffect(() => {
    if (!player || player.isDead || player.isResting) {
      console.log('Stamina regeneration skipped: player is null, dead, or resting', {
        playerExists: !!player,
        isDead: player?.isDead,
        isResting: player?.isResting,
      });
      return;
    }
  
    const regenerateStamina = () => {
      const now = Date.now();
      console.log('Stamina regeneration interval tick', {
        now,
        lastRegen,
        timeSinceLastRegen: now - lastRegen,
      });
    
      if (now - lastRegen < 5000) {
        console.log('Not enough time has passed for stamina regeneration');
        return;
      }
    
      setPlayer((prev) => {
        const staminaRegenRate = 5; // Increased to 5 Stamina every 5 seconds
        const maxStamina = calculateInitialStamina(prev.class);
        const newStamina = Math.min(maxStamina, prev.stamina + staminaRegenRate);
    
        console.log('Attempting stamina regeneration', {
          currentStamina: prev.stamina,
          maxStamina,
          staminaRegenRate,
          newStamina,
        });
    
        if (newStamina > prev.stamina) {
          setGameLog((prevLog) => [...prevLog, `Regenerated ${staminaRegenRate} Stamina!`]);
          console.log('Stamina regenerated', { newStamina });
        } else {
          console.log('Stamina not regenerated: already at or above max', {
            currentStamina: prev.stamina,
            newStamina,
          });
        }
    
        setLastRegen(now);
    
        return {
          ...prev,
          stamina: newStamina,
        };
      });
    };
  
    const interval = setInterval(regenerateStamina, 1000);
  
    return () => {
      console.log('Clearing stamina regeneration interval');
      clearInterval(interval);
    };
  }, [player?.isDead, player?.isResting]); // Only depend on isDead and isResting

  const handlePlayerDeath = async () => {
    setCombatLog((prev) => [...prev, 'You died!']);
    setPlayer((prev) => ({ ...prev, isDead: true }));
    setNeedsLeaderboardUpdate(true);
    await savePlayerStats();
  };

  const attack = useCallback(() => {
    if (!player || player.stamina <= 0 || player.isResting || player.isDead || (player.class === 'Mage' && player.mana < 2)) {
      setCombatLog((prev) => [...prev, 'Out of stamina, resting, dead, or insufficient mana! Cannot attack!']);
      return;
    }

    if (!enemies || enemies.length === 0) return;

    let closestEnemy = null;
    let minDistance = Infinity;
    let closestEnemyIndex = -1;

    enemies.forEach((enemy, index) => {
      const distance = Math.abs(player.x - enemy.x) + Math.abs(player.y - enemy.y);
      if (distance < minDistance) {
        minDistance = distance;
        closestEnemy = enemy;
        closestEnemyIndex = index;
      }
    });

    const range = player.range || 1;
    const isRanged = range > 1;

    if (minDistance > range) {
      setCombatLog((prev) => [...prev, `Closest enemy (${closestEnemy.type}) too far to attack! (Range: ${range} tile${range > 1 ? 's' : ''})`]);
      return;
    }

    setAttackTrigger((prev) => !prev);
    setCombatFlash('player');
    setTimeout(() => setCombatFlash(null), 200);

    let modifiedAtk = player.atk;
    let staminaCost = 1;

    const effectiveAccuracy = Math.max(0.5, player.accuracy * player.visibility);
    if (isRanged && Math.random() > effectiveAccuracy) {
      setCombatLog((prev) => [...prev, 'Ranged attack missed due to poor accuracy!']);
      setPlayer((prev) => ({
        ...prev,
        stamina: Math.max(0, Math.round(prev.stamina - staminaCost)),
        ...(player.class === 'Mage' && { mana: Math.max(0, Math.round(prev.mana - 2)) }),
      }));
      return;
    }

    if (zone?.effect === 'frost') modifiedAtk = Math.floor(modifiedAtk * 0.8);
    if (zone?.effect === 'veil') modifiedAtk = Math.floor(modifiedAtk * 0.9);

    const totalCritChance = player.crit_chance + (player.luck * 0.01);
    const isCrit = Math.random() < totalCritChance;
    if (isCrit) {
      modifiedAtk *= player.crit_damage + (player.luck * 0.1);
      setCombatLog((prev) => [...prev, 'Critical hit! Damage increased!']);
    }

    const enemyHitChance = Math.max(0.5, 1 - player.evasion);
    if (Math.random() > enemyHitChance) {
      setCombatLog((prev) => [...prev, 'Enemy dodged your attack!']);
      setPlayer((prev) => ({
        ...prev,
        stamina: Math.max(0, Math.round(prev.stamina - staminaCost)),
        ...(player.class === 'Mage' && { mana: Math.max(0, Math.round(prev.mana - 2)) }),
      }));
      return;
    }

    const dodgeRoll = Math.random();
    let enemyDamage = Math.max(0, closestEnemy.atk - player.def);
    if (dodgeRoll < player.dodge_chance) {
      enemyDamage = 0;
      setCombatLog((prev) => [...prev, 'Dodged enemy attack!']);
    }

    const effectiveEnemyDef = closestEnemy.def * (zone?.effect === 'guardian' ? 1.1 : 1);

    combat(player, closestEnemy, zone)
      .then((result) => {
        const newHp = Math.max(0, result.player.hp - enemyDamage);
        setPlayer((prev) => ({
          ...prev,
          ...result.player,
          hp: Math.round(newHp),
          stamina: Math.max(0, Math.round(prev.stamina - staminaCost)),
          ...(player.class === 'Mage' && { mana: Math.max(0, Math.round(prev.mana - 2)) }),
        }));

        const updatedEnemies = [...enemies];
        updatedEnemies[closestEnemyIndex] = { ...result.enemy, maxHp: closestEnemy.maxHp, def: effectiveEnemyDef };
        setEnemies(updatedEnemies);

        const logMessages = [
          `You ${isRanged ? 'shot' : 'hit'} ${closestEnemy.type} for ${Math.max(0, modifiedAtk - effectiveEnemyDef)}! ${closestEnemy.type} hit for ${enemyDamage}!`,
        ];
        setCombatLog((prev) => [...prev, ...logMessages]);

        if (newHp <= 0) {
          handlePlayerDeath();
        } else if (result.enemy.hp <= 0) {
          setCombatFlash(`enemy-${closestEnemyIndex}`);
          setTimeout(() => setCombatFlash(null), 200);
          applyLoot(player, player.luck * (player.lootBonus || 0)).then((loot) => {
            const xpGained = calculateXP(closestEnemy, player.level || 1);

            const currentXp = !isNaN(player.xp) ? player.xp : 0;
            const newXp = currentXp + xpGained;
            let newLevel = !isNaN(player.level) && player.level > 0 ? player.level : 1;
            let newMaxHp = player.maxHp;
            let newAtk = player.atk;
            let newDef = player.def;
            let newCritChance = player.crit_chance;
            let newCritDamage = player.crit_damage;
            let newMaxMana = player.max_mana;
            let newDodgeChance = player.dodge_chance;
            let newStatusResist = player.status_resist;
            let newLuck = player.luck;

            const xpRequired = 50 * newLevel + 10 * newLevel * newLevel;
            while (newXp >= xpRequired) {
              newLevel++;
              newMaxHp += 5;
              newAtk += 1;
              newDef += 1;
              newCritChance += 0.01;
              newCritDamage += 0.05;
              newMaxMana += 2;
              newDodgeChance += 0.01;
              newStatusResist += 0.02;
              newLuck += 1;
              setGameLog((prev) => [...prev, `Leveled up to ${newLevel}!`]);
              const nextXpRequired = 50 * newLevel + 10 * newLevel * newLevel;
              if (newXp >= nextXpRequired) {
                newXp -= xpRequired;
              } else {
                break;
              }
            }

            const hpRatio = player.hp / player.maxHp;
            const updatedHp = Math.round(Math.min(Math.round(newMaxHp * hpRatio), newMaxHp));

            let updatedQuests = [...player.activeQuests];
            updatedQuests.forEach((quest, index) => {
              if (quest.objective_type === 'kill' && quest.objective_target === closestEnemy.type) {
                updatedQuests[index] = { ...quest, progress: (updatedQuests[index].progress || 0) + 1 };
                checkQuestCompletion(updatedQuests[index], index);
              }
            });

            const enemyType = closestEnemy.monsterData.type;
            const enemyRarity = closestEnemy.rarity;
            const enemyName = closestEnemy.type;

            setPlayer((prev) => ({
              ...prev,
              gold: loot.player.gold,
              ectoplasm: loot.player.ectoplasm,
              kills: prev.kills + 1,
              xp: newLevel > (player.level || 1) ? newXp - xpRequired : newXp,
              level: newLevel,
              maxHp: Math.round(newMaxHp),
              atk: Math.round(newAtk),
              def: Math.round(newDef),
              crit_chance: Math.round(newCritChance * 100) / 100,
              crit_damage: Math.round(newCritDamage * 100) / 100,
              max_mana: Math.round(newMaxMana),
              dodge_chance: Math.round(newDodgeChance * 100) / 100,
              status_resist: Math.round(newStatusResist * 100) / 100,
              luck: Math.round(newLuck),
              hp: updatedHp,
              stamina: Math.max(0, Math.round(prev.stamina - staminaCost)),
              activeQuests: updatedQuests,
              killsByType: {
                ...prev.killsByType,
                [enemyType]: (prev.killsByType[enemyType] || 0) + 1,
              },
              killsByRarity: {
                ...prev.killsByRarity,
                [enemyRarity]: (prev.killsByRarity[enemyRarity] || 0) + 1,
              },
              killsByName: {
                ...prev.killsByName,
                [enemyName]: (prev.killsByName[enemyName] || 0) + 1,
              },
            }));

            const remainingEnemies = updatedEnemies.filter(e => e.hp > 0);
            setEnemies(remainingEnemies);
            setLastCombatEnd(Date.now());
            setCombatLog((prev) => [
              ...prev,
              `${closestEnemy.type} defeated! Gained ${loot.gold} gold and ${xpGained} XP! Kills: ${player.kills + 1}`,
            ]);
            setMobKilledTrigger((prev) => !prev);
            savePlayerStats();
            checkAchievements();

            if (remainingEnemies.length === 0) {
              setTimeout(() => {
                const newEnemies = spawnEnemyStatic(player.level, zone.effect, zone.id);
                if (newEnemies.length > 0) {
                  setEnemies(newEnemies);
                  setGameLog((prev) => [...prev, `${newEnemies.length} enemies have appeared: ${newEnemies.map(e => e.type).join(', ')}!`]);
                }
              }, 5000);
            }
          }).catch(() => {
            setGameLog((prev) => [...prev, 'Failed to process loot after combat']);
          });
        }
      })
      .catch(() => {
        setCombatLog((prev) => [...prev, 'Combat failed due to an error']);
      });
  }, [player, enemies, zone, setCombatLog, setAttackTrigger, setCombatFlash, setPlayer, setEnemies, setLastCombatEnd, setMobKilledTrigger, setGameLog, savePlayerStats, checkAchievements, handlePlayerDeath]);

  const checkQuestCompletion = (quest, index) => {
    if (quest.progress >= quest.objective_count) {
      setGameLog((prev) => [...prev, `Quest completed: ${quest.title}!`]);
      if (quest.reward_type === 'item') {
        const lootItems = getLootStatic();
        const rewardItem = lootItems.find((item) => item.name === quest.reward_value);
        if (rewardItem && player.inventory.length < 5) {
          setPlayer((prev) => ({
            ...prev,
            inventory: [...prev.inventory, { ...rewardItem, quantity: quest.reward_amount || 1 }],
          }));
          setGameLog((prev) => [
            ...prev,
            `Received ${quest.reward_amount || 1} ${quest.reward_value}(s)!`,
          ]);
        } else if (rewardItem) {
          setGameLog((prev) => [...prev, 'Inventory full! Cannot claim item reward']);
        } else {
          setGameLog((prev) => [...prev, `Reward item ${quest.reward_value} not found`]);
        }
      } else if (quest.reward_type === 'gold') {
        setPlayer((prev) => ({
          ...prev,
          gold: prev.gold + (quest.reward_amount || parseInt(quest.reward_value)),
        }));
        setGameLog((prev) => [
          ...prev,
          `Received ${quest.reward_amount || quest.reward_value} gold!`,
        ]);
      } else if (quest.reward_type === 'xp') {
        setPlayer((prev) => ({
          ...prev,
          xp: prev.xp + (quest.reward_amount || parseInt(quest.reward_value)),
        }));
        setGameLog((prev) => [
          ...prev,
          `Received ${quest.reward_amount || quest.reward_value} XP!`,
        ]);
      }
      setPlayer((prev) => ({
        ...prev,
        activeQuests: prev.activeQuests.filter((q, i) => i !== index),
        questsCompleted: (prev.questsCompleted || 0) + 1,
        questsCompletedInZone: {
          ...prev.questsCompletedInZone,
          [zone.id]: (prev.questsCompletedInZone[zone.id] || 0) + 1,
        },
      }));
      checkAchievements(); // Add this call to check achievements after completing a quest
    }
  };

  const interactNpc = (option) => {
    if (!player || player.stamina <= 0 || player.isResting || player.isDead) {
      setGameLog((prev) => [...prev, 'Out of stamina, resting, or dead! Cannot interact!']);
      setShowDialogue(false);
      return;
    }
  
    if (!npc || !npc.data) {
      setShowDialogue(false);
      return;
    }
  
    const npcData = npc.data;
    const npcId = npcData.id;
    const npcInteractions = player.npcInteractions[npcId] || {};
  
    let currentDialogue = npcData.dialogue[0];
    let availableOptions = currentDialogue.options || [];
  
    if (npcInteractions.dialogueState) {
      const { branchIndex, optionIndex } = npcInteractions.dialogueState;
      currentDialogue = npcData.dialogue[branchIndex];
      if (optionIndex !== undefined && currentDialogue.options[optionIndex].response) {
        currentDialogue = currentDialogue.options[optionIndex].response[0];
        availableOptions = currentDialogue.options || [];
      } else {
        availableOptions = currentDialogue.options || [];
      }
    }
  
    const zoneQuests = getQuestsByZone(zone.id);
    const availableQuests = zoneQuests.filter(
      (quest) =>
        npcData.available_quests?.includes(quest.id) &&
        !player.activeQuests.some((q) => q.id === quest.id)
    );
    if (availableQuests.length > 0) {
      availableOptions = [
        ...availableOptions,
        ...availableQuests.map((quest) => ({
          text: `Accept Quest: ${quest.title} (${quest.description})`,
          outcome: 'quest',
          questId: quest.id,
        })),
      ];
    }
  
    if (npcData.trade_items && npcData.trade_items.length > 0 && !npcInteractions.traded) {
      availableOptions = [
        ...availableOptions,
        ...npcData.trade_items.map((item) => ({
          text: `Buy: ${item.item_name} (${item.ectoplasm_cost} Ectoplasm)`,
          outcome: 'trade',
          tradeItem: item,
        })),
      ];
    }
  
    if (npcData.trade_items && npcData.trade_items.length > 0) {
      availableOptions = [
        ...availableOptions,
        {
          text: 'Sell an item from my inventory',
          outcome: 'sell',
        },
      ];
    }
  
    const selectedOption = availableOptions.find((opt) => opt.text === option.text);
    if (!selectedOption) {
      setGameLog((prev) => [...prev, 'That dialogue option is not available.']);
      setShowDialogue(false);
      return;
    }
  
    if (selectedOption.outcome === 'log') {
      setGameLog((prev) => [...prev, sanitizeInput(selectedOption.text)]);
      if (selectedOption.response) {
        setPlayer((prev) => ({
          ...prev,
          npcInteractions: {
            ...prev.npcInteractions,
            [npcId]: {
              ...prev.npcInteractions[npcId],
              dialogueState: { branchIndex: 0, optionIndex: availableOptions.indexOf(selectedOption) },
            },
          },
        }));
      } else {
        setShowDialogue(false);
      }
    } else if (selectedOption.outcome === 'trade' && selectedOption.tradeItem) {
      const tradeItem = { name: selectedOption.tradeItem.item_name, type: 'Consumable', hp: 10 };
      if (player.ectoplasm >= selectedOption.tradeItem.ectoplasm_cost) {
        if (player.inventory.length >= 5) {
          setGameLog((prev) => [...prev, 'Inventory full! Replace an item to trade']);
        } else {
          setPlayer((prev) => ({
            ...prev,
            ectoplasm: prev.ectoplasm - selectedOption.tradeItem.ectoplasm_cost,
            inventory: [...prev.inventory, tradeItem],
            stamina: Math.max(0, prev.stamina - 1),
            npcInteractions: {
              ...prev.npcInteractions,
              [npcId]: { ...prev.npcInteractions[npcId], traded: true },
            },
            npcTrades: (prev.npcTrades || 0) + 1, // Increment npcTrades
          }));
          setGameLog((prev) => [
            ...prev,
            `Traded ${selectedOption.tradeItem.ectoplasm_cost} Ectoplasm for a ${tradeItem.name}!`,
          ]);
          checkAchievements(); // Check achievements after trading
        }
      } else {
        setGameLog((prev) => [
          ...prev,
          `Need ${selectedOption.tradeItem.ectoplasm_cost} Ectoplasm to trade with ${npcData.name}!`,
        ]);
      }
      setShowDialogue(false);
    } else if (selectedOption.outcome === 'sell') {
      if (player.inventory.length === 0) {
        setGameLog((prev) => [...prev, 'You have no items to sell!']);
        setShowDialogue(false);
        return;
      }
      const itemToSell = player.inventory[0];
      const sellValue = 5;
      setPlayer((prev) => ({
        ...prev,
        inventory: prev.inventory.filter((_, i) => i !== 0),
        ectoplasm: prev.ectoplasm + sellValue,
        stamina: Math.max(0, prev.stamina - 1),
        npcTrades: (prev.npcTrades || 0) + 1, // Increment npcTrades
      }));
      setGameLog((prev) => [...prev, `Sold ${itemToSell.name} for ${sellValue} Ectoplasm!`]);
      setShowDialogue(false);
      checkAchievements(); // Check achievements after selling
    } else if (selectedOption.outcome === 'quest' && selectedOption.questId) {
      const quest = getQuestById(selectedOption.questId);
      if (quest && !player.activeQuests.find((q) => q.id === quest.id)) {
        setGameLog((prev) => [
          ...prev,
          `Quest accepted: ${quest.title} - ${quest.description}`,
        ]);
        setPlayer((prev) => ({
          ...prev,
          activeQuests: [...prev.activeQuests, { ...quest, progress: 0 }],
          npcInteractions: {
            ...prev.npcInteractions,
            [npcId]: { ...prev.npcInteractions[npcId], questAccepted: true },
          },
        }));
      }
      setShowDialogue(false);
    } else if (selectedOption.outcome === 'branch') {
      setPlayer((prev) => ({
        ...prev,
        npcInteractions: {
          ...prev.npcInteractions,
          [npcId]: {
            ...prev.npcInteractions[npcId],
            dialogueState: { branchIndex: 0, optionIndex: availableOptions.indexOf(selectedOption) },
          },
        },
      }));
    } else if (selectedOption.outcome === 'close') {
      setShowDialogue(false);
      setPlayer((prev) => ({
        ...prev,
        npcInteractions: {
          ...prev.npcInteractions,
          [npcId]: { ...prev.npcInteractions[npcId], dialogueState: null },
        },
      }));
    }
  };

  const handleItemUse = (item, index) => {
    if (!player || player.stamina <= 0 || player.isResting || player.isDead) {
      setGameLog((prev) => [...prev, 'Out of stamina, resting, or dead! Cannot use item!']);
      return;
    }
  
    if (item.type === 'Consumable') {
      if (item.hp) {
        setPlayer((prev) => ({
          ...prev,
          hp: Math.min(prev.hp + item.hp, prev.maxHp),
          inventory: prev.inventory.filter((_, i) => i !== index),
          stamina: Math.max(0, prev.stamina - 1),
          itemsUsedOrEquipped: (prev.itemsUsedOrEquipped || 0) + 1, // Increment itemsUsedOrEquipped
        }));
        setGameLog((prev) => [...prev, `Used ${item.name} to restore ${item.hp} HP!`]);
      } else if (item.mana) {
        setPlayer((prev) => ({
          ...prev,
          mana: Math.min(prev.max_mana, prev.mana + item.mana),
          inventory: prev.inventory.filter((_, i) => i !== index),
          stamina: Math.max(0, prev.stamina - 1),
          itemsUsedOrEquipped: (prev.itemsUsedOrEquipped || 0) + 1, // Increment itemsUsedOrEquipped
        }));
        setGameLog((prev) => [...prev, `Used ${item.name} to restore ${item.mana} Mana!`]);
      }
    } else if (item.equip_slot) {
      const slotMapping = {
        'main_hand': 'weapon',
        'ring': 'rings',
        'chest': 'chest',
        'head': 'head',
        'feet': 'feet',
        'off_hand': 'offHand',
        'neck': 'neck',
        'bracelet': 'bracelet',
        'hands': 'hands',
      };
      const slot = slotMapping[item.equip_slot] || item.equip_slot;
  
      setPlayer((prev) => {
        let newPlayer = { ...prev };
        let newInventory = [...newPlayer.inventory];
  
        if (!slot) {
          setGameLog((prev) => [...prev, `${item.name} cannot be equipped (no equip slot defined)`]);
          return prev;
        }
  
        if (newPlayer.equipped[slot]) {
          if (newInventory.length >= 5) {
            setGameLog((prev) => [...prev, `Cannot unequip ${newPlayer.equipped[slot].name}: Inventory full`]);
            return prev;
          }
          newInventory.push(newPlayer.equipped[slot]);
          setGameLog((prev) => [...prev, `Unequipped ${newPlayer.equipped[slot].name} and returned it to inventory`]);
  
          if (newPlayer.equipped[slot].atk) {
            newPlayer.atk -= newPlayer.equipped[slot].atk;
          }
          if (newPlayer.equipped[slot].def) {
            newPlayer.def -= newPlayer.equipped[slot].def;
          }
        }
  
        newPlayer.equipped[slot] = item;
        if (item.atk) {
          newPlayer.atk += item.atk;
          setGameLog((prev) => [...prev, `Equipped ${item.name} for +${item.atk} ATK in ${slot} slot!`]);
        }
        if (item.def) {
          newPlayer.def += item.def;
          setGameLog((prev) => [...prev, `Equipped ${item.name} for +${item.def} DEF in ${slot} slot!`]);
        }
  
        newPlayer.inventory = newInventory.filter((_, i) => i !== index);
        return {
          ...newPlayer,
          stamina: Math.max(0, newPlayer.stamina - 1),
          itemsUsedOrEquipped: (newPlayer.itemsUsedOrEquipped || 0) + 1, // Increment itemsUsedOrEquipped
        };
      });
    } else {
      setGameLog((prev) => [...prev, `${item.name} cannot be used or equipped`]);
    }
    setSelectedInventoryIndex(-1);
    checkAchievements(); // Check achievements after using or equipping an item
  };

  const handleItemUnequip = (slot) => {
    if (!player.equipped[slot]) {
      setGameLog((prev) => [...prev, `No item equipped in ${slot} slot`].slice(-5));
      return;
    }

    if (player.inventory.length >= 5) {
      setGameLog((prev) => [...prev, `Cannot unequip ${player.equipped[slot]?.name || 'item'}: Inventory full`].slice(-5));
      return;
    }

    setPlayer((prev) => {
      const newPlayer = { ...prev };
      const newInventory = [...newPlayer.inventory];
      const item = newPlayer.equipped[slot];

      newPlayer.atk = Math.max(0, (newPlayer.atk || 0) - (item.atk || 0));
      newPlayer.def = Math.max(0, (newPlayer.def || 0) - (item.def || 0));
      if (item.range) {
        newPlayer.range = Math.max(1, (newPlayer.range || 1) - item.range);
      } else if (slot === 'weapon' && !newPlayer.equipped[slot]?.range) {
        newPlayer.range = 1;
      }

      newInventory.push(item);
      newPlayer.equipped = { ...newPlayer.equipped, [slot]: null };
      const itemName = item?.name || 'unknown item';
      setGameLog((prev) => [...prev, `Unequipped ${itemName} from ${slot} slot`].slice(-5));

      newPlayer.inventory = newInventory;
      return newPlayer;
    });
  };

  const handleItemPickup = (itemName) => {
    let updatedQuests = [...player.activeQuests];
    updatedQuests.forEach((quest, index) => {
      if (quest.objective_type === 'collect' && quest.objective_target === itemName) {
        updatedQuests[index] = { ...quest, progress: (updatedQuests[index].progress || 0) + 1 };
        checkQuestCompletion(updatedQuests[index], index);
      }
    });
    setPlayer((prev) => ({ ...prev, activeQuests: updatedQuests }));
  };

  const handleBombUse = () => {
    if (!player || player.stamina <= 0 || player.isResting || player.isDead) {
      setGameLog((prev) => [...prev, 'Out of stamina, resting, or dead! Cannot use bomb!']);
      return;
    }
    const bomb = player.inventory.find(item => item.name === 'Bomb' || item.name === 'Ectoplasm Bomb');
    if (bomb) {
      applyBomb(bomb);
    } else {
      setGameLog((prev) => [...prev, 'No bomb found in inventory!']);
    }
  };



  const getEffectiveIncrement = (type, increment) => {
    if (!player || !player[type]) return increment;
    const currentValue = player[type] || 0;
    let effectiveIncrement = increment;

    if (['maxHp', 'atk', 'def', 'max_mana', 'luck'].includes(type)) {
      if (currentValue <= 50) effectiveIncrement = increment;
      else if (currentValue <= 100) effectiveIncrement *= 0.9;
      else if (currentValue <= 150) effectiveIncrement *= 0.8;
      else if (currentValue <= 200) effectiveIncrement *= 0.7;
      else effectiveIncrement *= 0.5;
    } else if (['crit_chance', 'dodge_chance', 'status_resist'].includes(type)) {
      if (currentValue <= 0.5) effectiveIncrement = increment;
      else if (currentValue <= 0.75) effectiveIncrement *= 0.9;
      else if (currentValue <= 1.0) effectiveIncrement *= 0.8;
      else if (currentValue <= 1.5) effectiveIncrement *= 0.7;
      else effectiveIncrement *= 0.5;
    } else if (type === 'crit_damage') {
      if (currentValue <= 1.0) effectiveIncrement = increment;
      else if (currentValue <= 1.5) effectiveIncrement *= 0.9;
      else if (currentValue <= 2.0) effectiveIncrement *= 0.8;
      else effectiveIncrement *= 0.7;
    }

    return Math.max(0.1, effectiveIncrement);
  };

  const buyUpgrade = (type, cost, increment) => {
    if (player.gold >= cost) {
      const currentValue = player[type] || 0;
      let effectiveIncrement = increment;
  
      if (['hp', 'atk', 'def', 'max_mana', 'luck'].includes(type)) {
        if (currentValue <= 50) effectiveIncrement = increment;
        else if (currentValue <= 100) effectiveIncrement *= 0.9;
        else if (currentValue <= 150) effectiveIncrement *= 0.8;
        else if (currentValue <= 200) effectiveIncrement *= 0.7;
        else effectiveIncrement *= 0.5;
      } else if (['crit_chance', 'dodge_chance', 'status_resist'].includes(type)) {
        if (currentValue <= 0.5) effectiveIncrement = increment;
        else if (currentValue <= 0.75) effectiveIncrement *= 0.9;
        else if (currentValue <= 1.0) effectiveIncrement *= 0.8;
        else if (currentValue <= 1.5) effectiveIncrement *= 0.7;
        else effectiveIncrement *= 0.5;
      } else if (type === 'crit_damage') {
        if (currentValue <= 1.0) effectiveIncrement = increment;
        else if (currentValue <= 1.5) effectiveIncrement *= 0.9;
        else if (currentValue <= 2.0) effectiveIncrement *= 0.8;
        else effectiveIncrement *= 0.7;
      }
  
      effectiveIncrement = Math.max(0.1, effectiveIncrement);
  
      setPlayer((prev) => {
        const newValue = (prev[type] || 0) + effectiveIncrement;
        return {
          ...prev,
          gold: prev.gold - cost,
          [type]: newValue,
          ...(type === 'max_mana' && { mana: Math.min(prev.mana, newValue) }),
          ...(type === 'maxHp' && { hp: Math.min(prev.hp, newValue) }),
        };
      });
      savePlayerStats();
      setGameLog((prev) => [
        ...prev,
        `Upgraded ${type.toUpperCase()} by ${effectiveIncrement.toFixed(2)}! (Cost: ${cost} Gold)`,
      ]);
    } else {
      setGameLog((prev) => [...prev, 'Not enough gold']);
    }
  };

  const craftBuff = (type, cost) => {
    if (player.ectoplasm >= cost) {
      setPlayer((prev) => {
        const newBoosts = { ...prev.tempBoosts || {} };
        let boostEffect = 0;

        switch (type) {
          case 'hpNextRun':
            boostEffect = 25;
            newBoosts.hpNextRun = (newBoosts.hpNextRun || 0) + boostEffect;
            setGameLog((prev) => [...prev, `Crafted +${boostEffect} HP for next run!`]);
            break;
          case 'atk5Floors':
            boostEffect = 10;
            newBoosts.atk5Floors = { value: boostEffect, turnsLeft: 5 };
            setGameLog((prev) => [...prev, `Crafted +${boostEffect} ATK for 5 floors!`]);
            break;
          case 'critChance5Floors':
            boostEffect = 0.10;
            newBoosts.critChance5Floors = { value: boostEffect, turnsLeft: 5 };
            setGameLog((prev) => [...prev, `Crafted +${(boostEffect * 100).toFixed(0)}% Crit Chance for 5 floors!`]);
            break;
          case 'dodgeChance5Floors':
            boostEffect = 0.10;
            newBoosts.dodgeChance5Floors = { value: boostEffect, turnsLeft: 5 };
            setGameLog((prev) => [...prev, `Crafted +${(boostEffect * 100).toFixed(0)}% Dodge Chance for 5 floors!`]);
            break;
          case 'def5Floors':
            boostEffect = 5;
            newBoosts.def5Floors = { value: boostEffect, turnsLeft: 5 };
            setGameLog((prev) => [...prev, `Crafted +${boostEffect} DEF for 5 floors!`]);
            break;
          default:
            return prev;
        }

        return {
          ...prev,
          ectoplasm: prev.ectoplasm - cost,
          tempBoosts: newBoosts,
        };
      });
      savePlayerStats();
    } else {
      setGameLog((prev) => [...prev, 'Not enough ectoplasm']);
    }
  };

  // Function to handle traveling to a waypoint
  const handleTravelToWaypoint = (waypoint) => {
    if (!isWaypointUnlocked(waypoint.id, player)) {
      setGameLog((prev) => [...prev, `Waypoint ${waypoint.name} is locked!`]);
      return;
    }

    // Check gold cost
    if (player.gold < waypoint.travel_cost) {
      setGameLog((prev) => [...prev, `Not enough gold to travel to ${waypoint.name}! (${waypoint.travel_cost} gold required)`]);
      return;
    }

    // Check cooldown
    const lastTravelTime = player.lastWaypointTravel?.[waypoint.id] || 0;
    const currentTime = Date.now() / 1000; // Current time in seconds
    if (currentTime - lastTravelTime < waypoint.travel_cooldown) {
      const remaining = Math.ceil(waypoint.travel_cooldown - (currentTime - lastTravelTime));
      setGameLog((prev) => [...prev, `Waypoint ${waypoint.name} is on cooldown! (${remaining}s remaining)`]);
      return;
    }

    // If traveling to the Hub Waypoint, reset the zone and floor
    if (waypoint.zone_id === null) {
      setIsLoading(true);
      setPlayer((prev) => ({
        ...prev,
        x: waypoint.position.x,
        y: waypoint.position.y,
        gold: prev.gold - waypoint.travel_cost,
        lastWaypointTravel: {
          ...prev.lastWaypointTravel,
          [waypoint.id]: currentTime,
        },
        zone: null, // Reset zone to indicate the player is in the Hub
        floorsCleared: 0,
      }));
      setFloorData(null);
      setEnemies([]);
      setNpc(null);
      setGameLog((prev) => [...prev, `Traveled to ${waypoint.name} for ${waypoint.travel_cost} gold!`]);
      setShowWaypoints(false);
      setIsLoading(false);
      return;
    }

    // Otherwise, travel to the waypoint's zone
    const targetZone = zones.find((z) => z.id === waypoint.zone_id);
    if (!targetZone) {
      setGameLog((prev) => [...prev, `Error: Zone for ${waypoint.name} not found!`]);
      return;
    }

    setIsLoading(true);
    const newFloor = generateFloorStatic(player.level, targetZone.id, player.floorsCleared);
    const newEnemies = spawnEnemyStatic(player.level, targetZone.effect, targetZone.id);

    setPlayer((prev) => ({
      ...prev,
      x: waypoint.position.x,
      y: waypoint.position.y,
      gold: prev.gold - waypoint.travel_cost,
      lastWaypointTravel: {
        ...prev.lastWaypointTravel,
        [waypoint.id]: currentTime,
      },
      zone: targetZone,
      floorsCleared: 0, // Reset floors cleared when traveling to a new zone
    }));
    setFloorData(newFloor);
    setZone(targetZone);
    setEnemies(newEnemies);
    setNpc(newFloor.npc);
    setGameLog((prev) => [
      ...prev,
      `Traveled to ${waypoint.name} for ${waypoint.travel_cost} gold!`,
      ...(newEnemies.length > 0 ? [`${newEnemies.length} enemies have appeared: ${newEnemies.map(e => e.type).join(', ')}!`] : []),
    ]);
    setShowWaypoints(false);
    setIsLoading(false);
    savePlayerStats();
  };

  useEffect(() => {
    if (!player || !player.tempBoosts) return;

    const applyBoosts = () => {
      let updatedPlayer = { ...player };
      const newBoosts = { ...player.tempBoosts };

      if (newBoosts.hpNextRun) {
        updatedPlayer.hp = Math.min(updatedPlayer.hp + newBoosts.hpNextRun, updatedPlayer.maxHp);
        delete newBoosts.hpNextRun;
      }
      ['atk5Floors', 'critChance5Floors', 'dodgeChance5Floors', 'def5Floors'].forEach((boostType) => {
        if (newBoosts[boostType] && newBoosts[boostType].turnsLeft > 0) {
          if (boostType === 'atk5Floors') updatedPlayer.atk += newBoosts[boostType].value;
          else if (boostType === 'critChance5Floors') updatedPlayer.crit_chance += newBoosts[boostType].value;
          else if (boostType === 'dodgeChance5Floors') updatedPlayer.dodge_chance += newBoosts[boostType].value;
          else if (boostType === 'def5Floors') updatedPlayer.def += newBoosts[boostType].value;
          newBoosts[boostType].turnsLeft -= 1;
          if (newBoosts[boostType].turnsLeft <= 0) delete newBoosts[boostType];
        }
      });

      setPlayer(updatedPlayer);
      if (Object.keys(newBoosts).length !== Object.keys(player.tempBoosts).length) {
        setPlayer((prev) => ({ ...prev, tempBoosts: newBoosts }));
        savePlayerStats();
      }
    };

    const interval = setInterval(applyBoosts, 1000);
    return () => clearInterval(interval);
  }, [player]);


  const returnToCrypt = async () => {
    try {
      setIsLoading(true);
      const newZone = generateZoneStatic(player?.level || 1, player?.zone?.id);
      const newFloor = generateFloorStatic(player?.level || 1, newZone.id, player?.floorsCleared || 0);
      const newEnemies = spawnEnemyStatic(player?.level || 1, newZone.effect, newZone.id);
  
      const hpRatio = player?.hp / player?.maxHp || 1;
      const newHp = player?.isDead ? player.maxHp : Math.min(player.maxHp * hpRatio, player.maxHp);
  
      setPlayer((prev) => ({
        ...prev,
        hp: newHp,
        mana: prev?.max_mana || 10,
        x: newEnemies.some(e => e.isBoss) ? 7 : 5,
        y: newEnemies.some(e => e.isBoss) ? 7 : 5,
        isDead: false,
        isResting: false,
        stamina: calculateInitialStamina(prev?.class || 'Warrior'),
        zone: newZone,
        tempBoosts: { ...prev.tempBoosts },
        zoneVisited: [...new Set([...(prev.zoneVisited || []), newZone.region])], // Update zoneVisited
      }));
      setFloorData(newFloor);
      setZone(newZone);
      setNpc(newFloor.npc);
      setEnemies(newEnemies);
      setCombatLog([]);
      setGameLog((prev) => [
        ...prev,
        `Returned to Crypt - Level ${player?.level || 1}`,
        ...(newEnemies.length > 0 ? [`${newEnemies.length} enemies have appeared: ${newEnemies.map(e => e.type).join(', ')}!`] : []),
      ]);
      checkAchievements(); // Check achievements after returning to crypt (for zoneVisited)
    } catch (error) {
      setGameLog((prev) => [...prev, 'Failed to return to crypt']);
    } finally {
      setIsLoading(false);
    }
  };

  const getNextZoneName = (player, zone, floorData) => {
    if (!zone || !floorData) return 'Unknown';
    const maxFloors = zone.level_range_max - zone.level_range_min + 1;
    if (player.floorsCleared >= maxFloors - 1 && floorData.floor.some(row => row.includes('portal'))) {
      const currentRegion = zone.region;
      const isCrypt = ['Crypt', 'Veilspires', 'Hollow Depths', 'Shattered Veil'].some(r => currentRegion.includes(r));
      const targetRegion = isCrypt ? 'Cursed Expanse Wastes' : 'Shattered Vestibule';
      const eligibleZones = zones.filter(z =>
        z.region === targetRegion &&
        player.level >= z.level_range_min &&
        player.level <= z.level_range_max
      );
      return eligibleZones[Math.floor(Math.random() * eligibleZones.length)]?.name || 'New Region';
    }
    return `Floor ${player.floorsCleared + 2}`;
  };

  useEffect(() => {
    let isMounted = true;
  
    const initializeGame = async () => {
      try {
        setIsLoading(true);
        if (!characterId) {
          setGameLog((prev) => [...prev, 'Failed to load character: No character ID']);
          return;
        }
  
        let initialPlayer = {
          hp: 10,
          maxHp: 10,
          atk: 2,
          def: 1,
          baseDef: 1,
          crit_chance: 0.05,
          crit_damage: 1.5,
          mana: 10,
          max_mana: 10,
          dodge_chance: 0.05,
          status_resist: 0.1,
          luck: 1,
          gold: 0,
          xp: 0,
          level: 1,
          x: 5,
          y: 5,
          class: selectedClass || 'Warrior',
          floorsCleared: 0,
          kills: 0,
          ectoplasm: 0,
          accuracy: 1.0,
          evasion: 0.0,
          visibility: 1.0,
          lootBonus: 0.0,
          inventory: [],
          equipped: {
            head: null,
            chest: null,
            hands: null,
            feet: null,
            offHand: null,
            weapon: null,
            neck: null,
            rings: null,
          },
          stamina: calculateInitialStamina(selectedClass || 'Warrior'),
          maxStamina: calculateInitialStamina(selectedClass || 'Warrior'), // Add maxStamina
          zone: null,
          isResting: false,
          isDead: false,
          activeQuests: [],
          playerId: characterId,
          tempBoosts: {},
          npcInteractions: {},
          killsByType: {},
          killsByRarity: {},
          killsByName: {},
          zoneVisited: [],
          questsCompleted: 0,
          questsCompletedInZone: {},
          npcTrades: 0,
          itemsUsedOrEquipped: 0,
          achievements: [],
          lastWaypointTravel: {}, // Add waypoint travel cooldowns
          unlockedWaypoints: [], // Add unlocked waypoints
        };
  
        const response = await authFetch(
          `https://cryptwalkergame-worker.patrick-vida.workers.dev/api/players?player_id=${characterId}`,
          { method: 'GET' }
        );
  
        if (response.ok) {
          const savedPlayer = await response.json();
          const playerLevelRaw = savedPlayer?.level;
          const playerLevel = !isNaN(playerLevelRaw) && playerLevelRaw > 0 ? playerLevelRaw : initialPlayer.level;
          const playerXpRaw = savedPlayer?.xp;
          const playerXp = !isNaN(playerXpRaw) && playerXpRaw >= 0 ? playerXpRaw : initialPlayer.xp;
  
          const expectedMaxHp = 10 + 5 * (playerLevel - 1);
  
          let loadedMaxHp = savedPlayer?.max_hp;
          if (loadedMaxHp === undefined || loadedMaxHp === null || isNaN(loadedMaxHp) || loadedMaxHp < 10) {
            loadedMaxHp = expectedMaxHp;
          }
  
          let loadedHp = savedPlayer?.hp;
          if (loadedHp === undefined || loadedHp === null || isNaN(loadedHp)) {
            loadedHp = loadedMaxHp;
          }
          loadedHp = Math.min(loadedHp, loadedMaxHp);
  
          if (loadedMaxHp !== expectedMaxHp) {
            loadedMaxHp = expectedMaxHp;
            loadedHp = Math.min(loadedHp, loadedMaxHp);
          }
  
          initialPlayer = {
            ...initialPlayer,
            ...(savedPlayer || {}),
            playerId: characterId,
            inventory: Array.isArray(savedPlayer?.inventory) ? savedPlayer.inventory : JSON.parse(savedPlayer?.inventory || '[]'),
            equipped: {
              head: savedPlayer?.equipped?.head || null,
              chest: savedPlayer?.equipped?.chest || null,
              hands: savedPlayer?.equipped?.hands || null,
              feet: savedPlayer?.equipped?.feet || null,
              offHand: savedPlayer?.equipped?.offHand || null,
              weapon: savedPlayer?.equipped?.weapon || null,
              neck: savedPlayer?.equipped?.neck || null,
              rings: savedPlayer?.equipped?.rings || null,
            },
            activeQuests: [],
            hp: loadedHp,
            maxHp: loadedMaxHp,
            atk: savedPlayer?.atk || initialPlayer.atk,
            def: savedPlayer?.def || initialPlayer.def,
            baseDef: savedPlayer?.baseDef || initialPlayer.baseDef,
            crit_chance: savedPlayer?.crit_chance || initialPlayer.crit_chance,
            crit_damage: savedPlayer?.crit_damage || initialPlayer.crit_damage,
            mana: savedPlayer?.mana || initialPlayer.mana,
            max_mana: savedPlayer?.max_mana || initialPlayer.max_mana,
            dodge_chance: savedPlayer?.dodge_chance || initialPlayer.dodge_chance,
            status_resist: savedPlayer?.status_resist || initialPlayer.status_resist,
            luck: savedPlayer?.luck || initialPlayer.luck,
            gold: savedPlayer?.gold || initialPlayer.gold,
            ectoplasm: savedPlayer?.ectoplasm || initialPlayer.ectoplasm,
            level: playerLevel,
            floorsCleared: savedPlayer?.floors_cleared || initialPlayer.floorsCleared,
            kills: savedPlayer?.kills || initialPlayer.kills,
            xp: playerXp,
            class: savedPlayer?.class || initialPlayer.class,
            tempBoosts: savedPlayer?.tempBoosts || {},
            npcInteractions: savedPlayer?.npcInteractions || {},
            x: 5,
            y: 5,
            killsByType: savedPlayer?.killsByType || initialPlayer.killsByType,
            killsByRarity: savedPlayer?.killsByRarity || initialPlayer.killsByRarity,
            killsByName: savedPlayer?.killsByName || initialPlayer.killsByName,
            zoneVisited: savedPlayer?.zoneVisited || initialPlayer.zoneVisited,
            questsCompleted: savedPlayer?.questsCompleted || initialPlayer.questsCompleted,
            questsCompletedInZone: savedPlayer?.questsCompletedInZone || initialPlayer.questsCompletedInZone,
            npcTrades: savedPlayer?.npcTrades || initialPlayer.npcTrades,
            itemsUsedOrEquipped: savedPlayer?.itemsUsedOrEquipped || initialPlayer.itemsUsedOrEquipped,
            achievements: savedPlayer?.achievements || initialPlayer.achievements,
            lastWaypointTravel: savedPlayer?.lastWaypointTravel || initialPlayer.lastWaypointTravel,
            unlockedWaypoints: savedPlayer?.unlockedWaypoints || initialPlayer.unlockedWaypoints,
          };
        } else {
          setGameLog((prev) => [...prev, `Failed to load character data. Using default values`]);
        }
  
        const newZone = generateZoneStatic(initialPlayer.level || 1, initialPlayer.zone?.id);
        const newFloor = generateFloorStatic(initialPlayer.level || 1, newZone.id, initialPlayer.floorsCleared || 0);
        const newEnemies = spawnEnemyStatic(initialPlayer.level || 1, newZone.effect, newZone.id);
  
        if (isMounted) {
          setPlayer({
            ...initialPlayer,
            zoneVisited: [...(initialPlayer.zoneVisited || []), newZone.region],
          });
          setFloorData(newFloor);
          setZone(newZone);
          setNpc(newFloor.npc);
          setEnemies(newEnemies);
          setGameLog((prev) => [...prev, 'Game initialized. Explore the crypt!']);
        }
      } catch (error) {
        if (isMounted) {
          setGameLog((prev) => [...prev, `Initialization error: ${error.message}. Using default values`]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
  
    initializeGame();
  
    return () => {
      isMounted = false;
    };
  }, [characterId, selectedClass, calculateInitialStamina]);

  useEffect(() => {
    if (!zone || !player || player.isDead || player.isResting) return;

    const interval = setInterval(() => {
      const now = Date.now();
      if (now - lastZoneEffect < 3000) return;

      setPlayer((prev) => {
        const zoneEffects = {
          none: { log: 'Neutral conditions, no effect applied.' },
          frost: {
            hp: -Math.max(0, 1 * (1 - prev.status_resist)),
            accuracy: -0.05,
            log: 'Frost chills you, dealing 1 damage and reducing accuracy by 5%!'
          },
          blood: {
            hp: -Math.max(0, 2 * (1 - prev.status_resist)),
            log: 'Blood aura drains 2 HP from you!'
          },
          dust: {
            accuracy: -0.1,
            log: 'Dust clouds obscure your vision, reducing accuracy by 10%!'
          },
          veil: {
            mana: -Math.max(0, 2 * (1 - prev.status_resist)),
            log: 'The veil saps 2 mana from you!'
          },
          water: {
            stamina: -Math.max(0, 1 * (1 - prev.status_resist)),
            log: 'Water slows you, draining 1 stamina!'
          },
          silence: {
            mana: -Math.max(0, 3 * (1 - prev.status_resist)),
            log: 'Silence stifles your magic, draining 3 mana!'
          },
          fire: {
            hp: -Math.max(0, 3 * (1 - prev.status_resist)),
            log: 'Fire burns you, dealing 3 damage!'
          },
          guardian: {
            hp: 2,
            log: 'The guardian’s presence heals you for 2 HP!'
          },
          illusion: {
            accuracy: -0.15,
            log: 'Illusions confuse you, reducing accuracy by 15%!'
          },
          soul: {
            mana: 2,
            log: 'Soul energy restores 2 mana!'
          },
          darkness: {
            accuracy: -0.2,
            log: 'Darkness blinds you, reducing accuracy by 20%!'
          },
          ectoplasm: {
            hp: -Math.max(0, 1 * (1 - prev.status_resist)),
            mana: -Math.max(0, 1 * (1 - prev.status_resist)),
            log: 'Ectoplasm drains 1 HP and 1 mana!'
          },
          greed: {
            stamina: -Math.max(0, 2 * (1 - prev.status_resist)),
            log: 'Greed exhausts you, draining 2 stamina!'
          },
          earth: {
            def: 0.05,
            log: 'Earth strengthens your defense by 5%!'
          },
          storm: {
            hp: -Math.max(0, 2 * (1 - prev.status_resist)),
            stamina: -Math.max(0, 1 * (1 - prev.status_resist)),
            log: 'The storm deals 2 damage and drains 1 stamina!'
          },
          heal: {
            hp: 3,
            log: 'A healing aura restores 3 HP!'
          },
          poison: {
            hp: -Math.max(0, 2 * (1 - prev.status_resist)),
            log: 'Poison seeps into you, dealing 2 damage!'
          },
          ghost: {
            mana: -Math.max(0, 2 * (1 - prev.status_resist)),
            accuracy: -0.1,
            log: 'Ghostly presence drains 2 mana and reduces accuracy by 10%!'
          }
        };

        const effect = zoneEffects[zone.effect] || zoneEffects.none;
        let newHp = prev.hp;
        let newStamina = prev.stamina;
        let newMana = prev.mana;
        let newAccuracy = prev.accuracy;
        let newDef = prev.def;

        if (effect.hp) newHp = Math.max(0, Math.min(prev.maxHp, prev.hp + effect.hp));
        if (effect.stamina) newStamina = Math.max(0, Math.min(prev.maxStamina, prev.stamina + effect.stamina));
        if (effect.mana) newMana = Math.max(0, Math.min(prev.maxMana, prev.mana + effect.mana));
        if (effect.accuracy) newAccuracy = Math.max(0, prev.accuracy + effect.accuracy);
        if (effect.def) newDef = Math.max(0, prev.def + effect.def);

        if (newHp <= 0) {
          handlePlayerDeath();
          return prev;
        }

        setGameLog((prevLog) => [...prevLog, effect.log]);
        setLastZoneEffect(now);

        return {
          ...prev,
          hp: newHp,
          stamina: newStamina,
          mana: newMana,
          accuracy: newAccuracy,
          def: newDef
        };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [zone, player, lastZoneEffect, handlePlayerDeath, setGameLog]);

  useEffect(() => {
    if (!enemies || enemies.length === 0 || !player || player.isDead) return;

    const interval = setInterval(() => {
      const now = Date.now();
      if (now - lastEnemyMove < 2000) return;

      const updatedEnemies = enemies.map(enemy => {
        const dx = player.x - enemy.x;
        const dy = player.y - enemy.y;
        let newEx = enemy.x;
        let newEy = enemy.y;

        if (Math.abs(dx) > Math.abs(dy)) {
          newEx += dx > 0 ? 1 : -1;
        } else {
          newEy += dy > 0 ? 1 : -1;
        }

        const mapSize = enemy.isBoss ? 15 : 10;

        if (
          newEx >= 0 &&
          newEx < mapSize &&
          newEy >= 0 &&
          newEy < mapSize &&
          floorData.floor[newEy][newEx] !== 'wall' &&
          !enemies.some(e => e !== enemy && e.x === newEx && e.y === newEy)
        ) {
          return { ...enemy, x: newEx, y: newEy };
        }
        return enemy;
      });

      setEnemies(updatedEnemies);
      setLastEnemyMove(now);
    }, 1000);

    return () => clearInterval(interval);
  }, [enemies, player, floorData, lastEnemyMove]);

  const handleMove = useCallback((key) => {
    if (!player || player.isDead) return;

    let newX = player.x;
    let newY = player.y;
    let staminaCost = 1;

    if (zone?.effect === 'veil') {
      staminaCost *= 1.1;
    }
    if (zone?.effect === 'water' || zone?.effect === 'earth') {
      staminaCost += 1;
    }

    const mapSize = enemies.some(e => e.isBoss) ? 15 : 10;

    if (['w', 'a', 's', 'd'].includes(key.toLowerCase()) && player.stamina >= staminaCost) {
      if (key === 'w') newY--;
      if (key === 's') newY++;
      if (key === 'a') newX--;
      if (key === 'd') newX++;

      if (newX >= 0 && newX < mapSize && newY >= 0 && newY < mapSize) {
        if (floorData.floor[newY][newX] !== 'wall') {
          const newFloorData = { ...floorData };
          let newGold = player.gold;
          let newEctoplasm = player.ectoplasm;
          let newInventory = [...player.inventory];

          if (floorData.floor[newY][newX] === 'exit') {
            setIsLoading(true);
            const newFloorsCleared = (player.floorsCleared || 0) + 1;
            const newFloor = generateFloorStatic(player.level, floorData.zoneId, enemies.some(e => e.isBoss) || false);
            const newZone = zone;
            const newEnemies = spawnEnemyStatic(player.level, newZone.effect, newZone.id);
            setPlayer((prev) => {
              setGameLog((prevLog) => [...prevLog, `Advanced to floor ${newFloorsCleared + 1}`]);
              return {
                ...prev,
                x: newEnemies.some(e => e.isBoss) ? 7 : 5,
                y: newEnemies.some(e => e.isBoss) ? 7 : 5,
                floorsCleared: newFloorsCleared,
                mana: prev.max_mana,
              };
            });
            setFloorData(newFloor);
            setZone(newZone);
            setEnemies(newEnemies);
            setGameLog((prev) => [
              ...prev,
              ...(newEnemies.length > 0 ? [`${newEnemies.length} enemies have appeared: ${newEnemies.map(e => e.type).join(', ')}!`] : []),
            ]);
            setStairsTrigger((prev) => !prev);
            savePlayerStats();
            setNeedsLeaderboardUpdate(true);
            setIsLoading(false);
            checkAchievements();
          } else if (floorData.floor[newY][newX] === 'portal') {
            const maxFloors = zone.level_range_max - zone.level_range_min + 1;
            if (player.floorsCleared >= maxFloors - 1) {
              setIsLoading(true);
              const newZone = zones.find(z =>
                z.region === (zone.region.includes('Crypt') || zone.region.includes('Veilspires') || zone.region.includes('Hollow Depths') || zone.region.includes('Shattered Veil')
                  ? 'Cursed Expanse Wastes'
                  : 'Shattered Vestibule') &&
                player.level >= z.level_range_min &&
                player.level <= z.level_range_max
              ) || zones[0];
              const newFloor = generateFloorStatic(player.level, newZone.id);
              const newEnemies = spawnEnemyStatic(player.level, newZone.effect, newZone.id);
              setPlayer((prev) => ({
                ...prev,
                x: 2,
                y: 2,
                zone: newZone,
                floorsCleared: 0,
                stamina: Math.min(prev.stamina + 5, calculateInitialStamina(prev.class)),
                mana: prev.max_mana,
                zoneVisited: [...new Set([...(prev.zoneVisited || []), newZone.region])],
              }));
              setFloorData(newFloor);
              setZone(newZone);
              setEnemies(newEnemies);
              setGameLog((prev) => [
                ...prev,
                `Travelled through portal to ${newZone.name}`,
                ...(newEnemies.length > 0 ? [`${newEnemies.length} enemies have appeared: ${newEnemies.map(e => e.type).join(', ')}!`] : []),
              ]);
              setStairsTrigger((prev) => !prev);
              setIsLoading(false);
              checkAchievements();
            } else {
              setGameLog((prev) => [...prev, 'Cannot use portal until zone is completed']);
            }
          } else if (floorData.floor[newY][newX] === 'npc') {
            setPlayer((prev) => ({
              ...prev,
              x: newX,
              y: newY,
              stamina: Math.max(0, Math.round(prev.stamina - staminaCost)),
            }));
            setShowDialogue(true);
            setMoveTrigger((prev) => !prev);
          } else {
            setPlayer((prev) => ({
              ...prev,
              x: newX,
              y: newY,
              stamina: Math.max(0, Math.round(prev.stamina - staminaCost)),
            }));
            setMoveTrigger((prev) => !prev);
          }
        }
      }
    }
  }, [player, zone, floorData, enemies, setPlayer, setGameLog, setMoveTrigger, setStairsTrigger, setIsLoading, setFloorData, setZone, setEnemies, savePlayerStats, checkAchievements, calculateInitialStamina]);

  useEffect(() => {
    if (!floorData || !player) return;

    const handleKey = async (e) => {
      if (!player || player.isDead) {
        if (e.key === 'r' || e.key === 'R') {
          returnToCrypt();
        }
        return;
      }

      if (['w', 'a', 's', 'd'].includes(e.key.toLowerCase())) {
        handleMove(e.key.toLowerCase());
      } else if (e.key.toLowerCase() === 'e' && floorData.floor[player.y][player.x] === 'loot' && player.stamina >= 1) {
        const newFloorData = { ...floorData };
        let newGold = player.gold;
        let newEctoplasm = player.ectoplasm;
        let newInventory = [...player.inventory];

        applyLoot(player, player.luck).then((loot) => {
          newGold += loot.gold;
          newFloorData.floor[player.y][player.x] = 'floor';
          setGameLog((prev) => [...prev, `Picked up ${loot.gold} gold!`]);
          setPickupTrigger((prev) => !prev);
        }).catch(() => {
          setGameLog((prev) => [...prev, 'Failed to process loot']);
        }).finally(() => {
          setPlayer((prev) => ({
            ...prev,
            gold: newGold,
            ectoplasm: newEctoplasm,
            inventory: newInventory,
            stamina: Math.max(0, Math.round(prev.stamina - 1)),
          }));
          setFloorData(newFloorData);
        });
      } else if (e.key.toLowerCase() === ' ') {
        attack();
      } else if (e.key.toLowerCase() === 'b') {
        handleBombUse();
      } else if (e.key.toLowerCase() === 'u' && player && selectedInventoryIndex >= 0 && selectedInventoryIndex < player.inventory.length) {
        handleItemUse(player.inventory[selectedInventoryIndex], selectedInventoryIndex);
      } else if (e.key.toLowerCase() === 'i') {
        setSelectedInventoryIndex((prev) => {
          const nextIndex = (prev + 1) % (player?.inventory.length || 1);
          const newIndex = nextIndex === player?.inventory.length ? -1 : nextIndex;
          if (newIndex === -1) {
            setGameLog((prev) => [...prev, 'Inventory selection cleared']);
          } else {
            setGameLog((prev) => [
              ...prev,
              `Selected ${player.inventory[newIndex]?.name || 'none'} (press U to use)`,
            ]);
          }
          return newIndex;
        });
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [
    floorData,
    player,
    zone,
    selectedInventoryIndex,
    lastCombatEnd,
    enemies,
    calculateInitialStamina,
    returnToCrypt,
    attack,
    handleMove,
    handleBombUse,
    handleItemUse,
    setGameLog,
    setPickupTrigger,
    setFloorData,
  ]);

  useEffect(() => {
    // Ensure showHub is false on initial render
    setShowHub(false);
  }, []); // Empty dependency array to run only on mount
  
  // Keep the existing useEffect for debugging showHub changes
  useEffect(() => {
    console.log('showHub changed to:', showHub);
  }, [showHub]);

  if (isLoading || !player || !floorData || !floorData.floor) {
    return <div className="game"><p>Loading game...</p></div>;
  }

  return (
    <div className="game">
      <AudioManager
        onMobKilled={mobKilledTrigger}
        onAttack={attackTrigger}
        onMove={moveTrigger}
        onStairs={stairsTrigger}
        onPickup={pickupTrigger}
        currentZone={zone}
      />
      <header className="game-header">
        <h1>{zone.name}</h1>
        {zone && (
          <div className="zone-details">
            <p><strong>Description:</strong> {sanitizeInput(zone.description)}</p>
            <p><strong>Hazards:</strong> {typeof zone.hazards === 'string' && zone.hazards.startsWith('[') ? JSON.parse(zone.hazards).map(sanitizeInput).join(', ') : sanitizeInput(zone.hazards)}</p>
            <p><strong>Enemy Types:</strong> {typeof zone.enemy_types === 'string' && zone.enemy_types.startsWith('[') ? JSON.parse(zone.enemy_types).map(sanitizeInput).join(', ') : sanitizeInput(zone.enemy_types)}</p>
          </div>
        )}
        <nav className="game-nav">
          <button className="nav-button" onClick={() => setShowInstructions(!showInstructions)}>
            {showInstructions ? 'Hide Instructions' : 'Show Instructions'}
          </button>
          <button className="nav-button" onClick={() => setShowHub(!showHub)}>
            {showHub ? 'Hide Hub' : 'Show Hub'}
          </button>
          <button className="nav-button" onClick={() => setShowQuests(!showQuests)}>
            {showQuests ? 'Hide Quests' : 'Show Quests'}
          </button>
          <button className="nav-button" onClick={() => setShowInventory(!showInventory)}>
            {showInventory ? 'Hide Inventory' : 'Show Inventory'}
          </button>
          <button className="nav-button" onClick={() => setShowAchievements(!showAchievements)}>
            {showAchievements ? 'Hide Achievements' : 'Show Achievements'}
          </button>
          <button className="nav-button" onClick={() => setShowWaypoints(!showWaypoints)}>
            {showWaypoints ? 'Hide Waypoints' : 'Show Waypoints'}
          </button>
          <button className="nav-button" onClick={async () => {
            await savePlayerStats();
            localStorage.removeItem('userId');
            localStorage.removeItem('characterId');
            localStorage.removeItem('isLoggedIn');
            localStorage.removeItem('currentUsername');
            localStorage.removeItem('token');
            setGameState('start');
          }}>
            Logout
          </button>
        </nav>
      </header>
      {!player.isDead ? (
        <div className="game-body">
          <aside className="game-sidebar game-stats">
          <section className="combat-section">
            {enemies.length > 0 && (
              <div className="combat-actions">
                <button className="action-button" onClick={attack}>
                  Attack (Range: {player.range || 1} tile{player.range > 1 ? 's' : ''})
                </button>
                {player.inventory.some(
                  (item) => item.name === 'Bomb' || item.name === 'Ectoplasm Bomb'
                ) && (
                  <button className="action-button" onClick={handleBombUse}>
                    Use Bomb
                  </button>
                )}
              </div>
            )}
            <div className="health-bars">
              {enemies.map((enemy, index) => (
                enemy.hp > 0 && (
                  <div key={index} className="enemy-stats-container">
                    <p>
                      <strong>{sanitizeInput(enemy.type)}</strong> ({enemy.rarity}, Lv {enemy.level_min}-{enemy.level_max})
                    </p>
                    <div className="stat-bar">
                      <p>HP:</p>
                      <HealthBar current={enemy.hp} max={enemy.maxHp} color="#ff4040" />
                    </div>
                    {enemy.maxMana > 0 && (
                      <div className="stat-bar">
                        <p>Mana:</p>
                        <HealthBar current={enemy.mana} max={enemy.maxMana} color="#4040ff" />
                      </div>
                    )}
                    <p>ATK: {enemy.atk} | DEF: {enemy.def}</p>
                  </div>
                )
              ))}
            </div>
          </section>
            <section className="logs-section">
              <div className="combat-log">
                <h4>Combat Log</h4>
                <ul>
                  {combatLog.slice(-5).reverse().map((log, index) => (
                    <li key={index}>{sanitizeInput(log)}</li>
                  ))}
                </ul>
              </div>
              <div className="game-log">
                <h4>Game Log</h4>
                <ul>
                  {gameLog.slice(-5).reverse().map((log, index) => (
                    <li key={index}>{sanitizeInput(log)}</li>
                  ))}
                </ul>
              </div>
            </section>
          </aside>
          <main className="game-main">
            {showDialogue && npc && (
              <DialogueBubble
                npc={npc}
                onSelectOption={interactNpc}
                onClose={() => setShowDialogue(false)}
                player={player}
                zone={zone}
              />
            )}
            <section className="map-section">
              <ShapeMap
                floor={floorData.floor}
                player={player}
                enemies={enemies}
                npc={floorData.npc}
                zoneEffect={zone.effect}
                combatFlash={combatFlash}
                nextZoneName={getNextZoneName(player, zone, floorData)}
                attackTrigger={attackTrigger}
                enemyAttackTrigger={enemyAttackTrigger}
                isBoss={enemies.some(e => e.isBoss) || false}
              />
              <p className="zone-effect">
                Effect: <span title={sanitizeInput(zone.effect_description)}>{sanitizeInput(zone.effect_description)}</span>
              </p>
            </section>
          </main>
          <aside className="game-sidebar game-profile">
            <CharacterProfile
              player={player}
              setPlayer={setPlayer}
              setGameLog={setGameLog}
              onUnequip={handleItemUnequip}
            />
          </aside>
          <div className={`modal instructions-modal ${showInstructions ? 'active' : ''}`}>
            <div className="modal-content">
              <Instructions />
              <button className="action-button" onClick={() => setShowInstructions(false)}>Close</button>
            </div>
          </div>
          <div className={`modal achievements-modal ${showAchievements ? 'active' : ''}`}>
            <div className="modal-content">
              <div className="panel-header">
                <h3>Achievements</h3>
                <button className="collapse-toggle" onClick={() => setShowAchievements(false)}>
                  Collapse
                </button>
              </div>
              <ul>
                {achievements.map((achievement) => {
                  let progress = 0;
                  if (achievement.target === "kills") progress = player.kills || 0;
                  else if (achievement.target === "killsByType") progress = player.killsByType[achievement.targetSubType] || 0;
                  else if (achievement.target === "killsByRarity") progress = player.killsByRarity[achievement.targetSubType] || 0;
                  else if (achievement.target === "killsByName") progress = player.killsByName[achievement.targetSubType] || 0;
                  else if (achievement.target === "floorsCleared") progress = player.floorsCleared || 0;
                  else if (achievement.target === "zoneVisited") progress = player.zoneVisited.includes(achievement.zoneRegion) ? 1 : 0;
                  else if (achievement.target === "questsCompleted") progress = player.questsCompleted || 0;
                  else if (achievement.target === "questsCompletedInZone") progress = player.questsCompletedInZone[achievement.zoneId] || 0;
                  else if (achievement.target === "gold") progress = player.gold || 0;
                  else if (achievement.target === "ectoplasm") progress = player.ectoplasm || 0;
                  else if (achievement.target === "level") progress = player.level || 1;
                  else if (achievement.target === "npcTrades") progress = player.npcTrades || 0;
                  else if (achievement.target === "itemsUsedOrEquipped") progress = player.itemsUsedOrEquipped || 0;
  
                  const isCompleted = player.achievements?.includes(achievement.id);
                  const progressPercentage = Math.min((progress / achievement.targetValue) * 100, 100);
  
                  return (
                    <li key={achievement.id} className="achievement-item">
                      <div className="achievement-header">
                        <strong>{achievement.name}</strong>
                        {isCompleted && <span className="achievement-completed">Completed!</span>}
                      </div>
                      <div className="achievement-description">{achievement.description}</div>
                      <div className="achievement-progress">
                        <p>
                          Progress: {progress}/{achievement.targetValue}
                        </p>
                        <progress
                          className="achievement-progress-bar"
                          value={progress}
                          max={achievement.targetValue}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
              <button className="action-button" onClick={() => setShowAchievements(false)}>
                Close Achievements
              </button>
            </div>
          </div>
          {showHub && (
            <div className={`modal hub-modal ${showHub ? 'active' : ''}`}>
              <Hub
                setGameState={setGameState}
                playerId={player?.playerId}
                player={player}
                setPlayer={setPlayer}
                setShowHub={setShowHub}
                setGameLog={setGameLog}
              />
            </div>
          )}
          <div className={`modal quests-modal ${showQuests ? 'active' : ''}`}>
            <div className="modal-content">
              <div className="panel-header">
                <h3>Active Quests</h3>
                <button className="collapse-toggle" onClick={() => setShowQuests(false)}>
                  Collapse
                </button>
              </div>
              {player.activeQuests.length === 0 ? (
                <p>No active quests.</p>
              ) : (
                <Quests activeQuests={player.activeQuests} setPlayer={setPlayer} setGameLog={setGameLog} />
              )}
              <button className="action-button" onClick={() => setShowQuests(false)}>
                Close Quests
              </button>
            </div>
          </div>
          <div className={`modal inventory-modal ${showInventory ? 'active' : ''}`}>
            <div className="modal-content">
              <div className="panel-header">
                <h3>Inventory (Max {player.inventory.length}/5 slots)</h3>
                <button className="collapse-toggle" onClick={() => setShowInventory(false)}>
                  Collapse
                </button>
              </div>
              <Inventory
                inventory={player.inventory}
                setPlayer={setPlayer}
                setLog={setGameLog}
                canSell={false}
                playerLevel={player.level}
              />
              <button className="action-button" onClick={() => setShowInventory(false)}>
                Close Inventory
              </button>
            </div>
          </div>
          <div className={`modal waypoints-modal ${showWaypoints ? 'active' : ''}`}>
            <div className="modal-content">
              <div className="panel-header">
                <h3>Waypoints</h3>
                <button className="collapse-toggle" onClick={() => setShowWaypoints(false)}>
                  Collapse
                </button>
              </div>
              <ul>
                {waypoints.map((waypoint) => {
                  const isUnlocked = isWaypointUnlocked(waypoint.id, player);
                  const lastTravelTime = player.lastWaypointTravel?.[waypoint.id] || 0;
                  const currentTime = Date.now() / 1000;
                  const onCooldown = currentTime - lastTravelTime < waypoint.travel_cooldown;
                  const remainingCooldown = onCooldown
                    ? Math.ceil(waypoint.travel_cooldown - (currentTime - lastTravelTime))
                    : 0;
  
                  return (
                    <li key={waypoint.id} className="waypoint-item">
                      <div className="waypoint-header">
                        <strong>{waypoint.name}</strong>
                        {waypoint.zone_id ? ` (Zone ${waypoint.zone_id})` : ' (Hub)'}
                      </div>
                      {isUnlocked ? (
                        <button
                          className="action-button"
                          onClick={() => handleTravelToWaypoint(waypoint)}
                          disabled={onCooldown || player.gold < waypoint.travel_cost}
                        >
                          Travel ({waypoint.travel_cost} Gold)
                          {onCooldown && ` - Cooldown: ${remainingCooldown}s`}
                          {!onCooldown && player.gold < waypoint.travel_cost && ' - Not Enough Gold'}
                        </button>
                      ) : (
                        <p>Locked (Level {waypoint.level_requirement} required)</p>
                      )}
                    </li>
                  );
                })}
              </ul>
              <button className="action-button" onClick={() => setShowWaypoints(false)}>
                Close Waypoints
              </button>
            </div>
          </div>
          {isMobile && (
            <TouchControls
              onMove={handleMove}
              onAttack={attack}
              onInteract={() => {
                if (floorData.floor[player.y][player.x] === 'loot' && player.stamina >= 1) {
                  const newFloorData = { ...floorData };
                  let newGold = player.gold;
                  let newEctoplasm = player.ectoplasm;
                  let newInventory = [...player.inventory];
  
                  applyLoot(player, player.luck).then((loot) => {
                    newGold += loot.gold;
                    newFloorData.floor[player.y][player.x] = 'floor';
                    setGameLog((prev) => [...prev, `Picked up ${loot.gold} gold!`]);
                    setPickupTrigger((prev) => !prev);
                  }).catch(() => {
                    setGameLog((prev) => [...prev, 'Failed to process loot']);
                  }).finally(() => {
                    setPlayer((prev) => ({
                      ...prev,
                      gold: newGold,
                      ectoplasm: newEctoplasm,
                      inventory: newInventory,
                      stamina: Math.max(0, Math.round(prev.stamina - 1)),
                    }));
                    setFloorData(newFloorData);
                  });
                }
              }}
              onBomb={handleBombUse}
              onInventoryCycle={() => {
                setSelectedInventoryIndex((prev) => {
                  const nextIndex = (prev + 1) % (player?.inventory.length || 1);
                  const newIndex = nextIndex === player?.inventory.length ? -1 : nextIndex;
                  if (newIndex === -1) {
                    setGameLog((prev) => [...prev, 'Inventory selection cleared']);
                  } else {
                    setGameLog((prev) => [
                      ...prev,
                      `Selected ${player.inventory[newIndex]?.name || 'none'} (press U to use)`,
                    ]);
                  }
                  return newIndex;
                });
              }}
              onUseItem={() => {
                if (player && selectedInventoryIndex >= 0 && selectedInventoryIndex < player.inventory.length) {
                  handleItemUse(player.inventory[selectedInventoryIndex], selectedInventoryIndex);
                }
              }}
            />
          )}
        </div>
      ) : (
        <div className="game-over">
          <h2>Game Over</h2>
          <div className="game-over-section game-over-stats">
            <h3>Your Journey</h3>
            <p>Floors Cleared: {player.floorsCleared}</p>
            <p>Total Kills: {player.kills}</p>
            <p>Gold Collected: {player.gold}</p>
          </div>
          <div className="game-over-section game-over-actions">
            <h3>What’s Next?</h3>
            <div className="action-buttons">
              <button className="action-button share-button" onClick={shareOnX}>
                Share on X
              </button>
              <button className="action-button return-button" onClick={returnToCrypt}>
                Return to Crypt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Game;